// FotoGallery.js
import React, { useState } from 'react';
import {
  Box, Typography, Chip, IconButton, Dialog, DialogContent, DialogTitle,
  ImageList, ImageListItem, ImageListItemBar, Button,
  Stack, Divider
} from '@mui/material';
import {
  Close, ZoomIn, PhotoCamera, CheckCircle,
  Schedule, Block, Image as ImageIcon, ExpandMore
} from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

function getFileBase(filename) {
  if (!filename) return 'images';
  if (filename.startsWith('admb_')) return 'adm-bantuan';
  if (filename.startsWith('rabadm_')) return 'rab-progress-administrasi';
  if (filename.startsWith('rabfisik_')) return 'rab-progress-fisik';
  if (filename.startsWith('rabprogress_')) return 'rab-progress';
  if (filename.startsWith('rab_')) return 'rab';
  return 'images';
}

function buildFileUrl(filename) {
  return `${BASE_URL}/files/${getFileBase(filename)}/${filename}`;
}

// ============================================================
// KOMPONEN LIGHTBOX
// ============================================================
const LightboxDialog = ({ open, onClose, images, initialIndex }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);

  if (!images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)', height: '100vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
        <Typography variant="subtitle1">
          {currentImage?.label || 'Foto'} ({currentIndex + 1}/{images.length})
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
        <Box sx={{ width: '100%', height: '80vh' }}>
          <Swiper
            modules={[Navigation, Pagination, Zoom]}
            navigation
            pagination={{ clickable: true }}
            zoom={true}
            initialSlide={currentIndex}
            onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
            style={{ height: '100%', width: '100%' }}
          >
            {images.map((img, index) => (
              <SwiperSlide key={index}>
                <div className="swiper-zoom-container">
                  <img src={img.url} alt={img.label || 'Foto'} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </div>
                <Box sx={{ position: 'absolute', bottom: 60, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.7)', color: 'white', px: 2, py: 1, borderRadius: 1, textAlign: 'center', maxWidth: '80%' }}>
                  <Typography variant="body2">{img.label}</Typography>
                  {img.catatan && <Typography variant="caption" display="block" color="text.secondary">{img.catatan}</Typography>}
                  {img.status && <Chip size="small" label={img.status} color={img.status === 'Selesai' ? 'success' : img.status === 'Sedang Berlangsung' ? 'warning' : 'default'} sx={{ mt: 0.5 }} />}
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// KOMPONEN FOTO GALLERY - Grouped per step
// ============================================================
const FotoGallery = ({ langkah, title = 'Dokumentasi Pengerjaan', fileBase: forcedBase }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentGroupImages, setCurrentGroupImages] = useState([]);

  // Group photos by step
  const stepGroups = (langkah || []).map((step) => {
    if (!step.foto) return null;
    let fotos = [];
    try {
      fotos = typeof step.foto === 'string' ? JSON.parse(step.foto) : step.foto;
    } catch { return null; }
    if (!Array.isArray(fotos) || fotos.length === 0) return null;
    const imgs = fotos.map((f) => ({
      url: forcedBase ? `${BASE_URL}/files/${forcedBase}/${f}` : buildFileUrl(f),
      label: step.tahap || 'Foto',
      catatan: step.catatan || '',
      status: step.status || '',
      tanggal: step.tanggalSelesai || step.createdAt,
    }));
    return { step, images: imgs };
  }).filter(Boolean);

  const allImages = stepGroups.flatMap(g => g.images);

  if (allImages.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <ImageIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">Belum ada dokumentasi foto</Typography>
      </Box>
    );
  };

  const handleOpenLightbox = (images, index) => {
    setCurrentGroupImages(images);
    setSelectedIndex(index);
    setLightboxOpen(true);
  };

  const handleOpenAll = () => {
    setCurrentGroupImages(allImages);
    setSelectedIndex(0);
    setLightboxOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhotoCamera />
          {title} ({allImages.length} foto)
        </Typography>
        {allImages.length > 8 && (
          <Button size="small" variant="outlined" startIcon={<ImageIcon />} onClick={handleOpenAll}>
            Lihat Semua
          </Button>
        )}
      </Box>

      {stepGroups.map((group, gi) => (
        <Box key={gi} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip size="small" label={gi + 1} color="primary" />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {group.step.tahap} ({group.images.length} foto)
            </Typography>
            {group.step.status === 'Selesai' && <CheckCircle fontSize="small" color="success" />}
            {group.step.status === 'Sedang Berlangsung' && <Schedule fontSize="small" color="warning" />}
          </Box>
          <ImageList sx={{ m: 0, gridTemplateColumns: { xs: 'repeat(2, 1fr) !important', sm: 'repeat(3, 1fr) !important', md: 'repeat(6, 1fr) !important' } }} gap={6}>
            {group.images.map((item, index) => (
              <ImageListItem
                key={index}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  overflow: 'hidden',
                  '&:hover': { transform: 'scale(1.03)', transition: 'transform 0.2s', boxShadow: 3 },
                }}
                onClick={() => handleOpenLightbox(group.images, index)}
              >
                <img
                  src={item.url}
                  alt={item.label}
                  loading="lazy"
                  style={{ width: '100%', height: 100, objectFit: 'cover' }}
                  onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24"%3E%3Cpath fill="%23ccc" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/%3E%3C/svg%3E'; }}
                />
                <ImageListItemBar
                  position="bottom"
                  sx={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }}
                  subtitle={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {item.status === 'Selesai' && <CheckCircle fontSize="small" sx={{ fontSize: 12 }} />}
                      <Typography variant="caption" sx={{ fontSize: 10 }}>{item.status || ''}</Typography>
                    </Box>
                  }
                />
                <IconButton
                  sx={{ position: 'absolute', top: 2, right: 2, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleOpenLightbox(group.images, index); }}
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
              </ImageListItem>
            ))}
          </ImageList>
          {gi < stepGroups.length - 1 && <Divider sx={{ mt: 1 }} />}
        </Box>
      ))}

      <LightboxDialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={currentGroupImages}
        initialIndex={selectedIndex}
      />
    </Box>
  );
};

export default FotoGallery;
